import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

// Same shape for every tone — a tinted background, no border, colored text —
// so a success and an error message only differ by color, never by weight.
const toneClasses: Record<AlertTone, string> = {
  info: 'bg-mint-tint text-ink',
  success: 'bg-mint-tint text-pine',
  warning: 'bg-warning-bg text-warning',
  danger: 'bg-danger/10 text-danger',
};

/** A banner-style message — vervangt `.pending-consents`, `.config-warning`,
 * `.submit-error`, `.submit-warning`. */
export function Alert({
  tone = 'info',
  children,
  className,
  role,
}: {
  tone?: AlertTone;
  children?: ReactNode;
  className?: string;
  /** Defaults to `"alert"` for warning/danger and `"status"` for info/success. */
  role?: 'alert' | 'status';
}) {
  return (
    <div
      role={role ?? (tone === 'info' || tone === 'success' ? 'status' : 'alert')}
      className={cn('rounded-2xl px-4 py-3 text-sm', toneClasses[tone], className)}
    >
      {children}
    </div>
  );
}
